import { Injectable } from '@angular/core';
import { Observable, map, of, tap, BehaviorSubject } from 'rxjs';
import { IGetSqlDataParams, SqlResponse } from '@cdw-ae/filter-bar-interfaces';
import { applyActiveFilters } from '@cdw-ae/filter-bar-client-side-matching';
import { ICdwSearchGridDataService } from '@cdw-ae/common-ui-components-search-grid';
import { GopherBuddySession } from './gopher-buddy-session.interface';
import { DevicesDataService } from '../../../../../../common/services/device/devices-data.service';

/**
 * Data service for Gopher Buddy Sessions
 *
 * Uses the getDeviceSessions API endpoint which fetches session data from BigQuery.
 * The full (unfiltered) session list for the current device is fetched once and
 * cached; the search grid's filter bar and pagination are applied client-side
 * against that cache so filter changes don't re-hit the endpoint.
 */
@Injectable({
  providedIn: 'root',
})
export class GopherBuddySessionsDataService implements ICdwSearchGridDataService<GopherBuddySession> {
  private deviceId: string | null = null;
  private cachedDeviceId: string | null = null;
  private cachedSessions: GopherBuddySession[] | null = null;
  private isEmptySubject = new BehaviorSubject<boolean>(false);
  public isEmpty$ = this.isEmptySubject.asObservable();
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  public isLoading$ = this.isLoadingSubject.asObservable();

  constructor(private devicesDataService: DevicesDataService) {}

  /**
   * Set the device ID for fetching sessions
   */
  setDeviceId(deviceId: string | null): void {
    if (deviceId !== this.deviceId) {
      this.cachedDeviceId = null;
      this.cachedSessions = null;
    }
    this.deviceId = deviceId;
  }

  /**
   * Fetches session data for the search grid
   *
   * @param params - Filter and pagination parameters
   * @returns Observable of paginated session data
   */
  fetchSqlData(params: IGetSqlDataParams): Observable<SqlResponse<GopherBuddySession>> {
    if (!this.deviceId) {
      this.isEmptySubject.next(true);
      this.isLoadingSubject.next(false);
      return of({ data: [], itemTotal: 0 });
    }

    const sessions$ =
      this.cachedSessions && this.cachedDeviceId === this.deviceId
        ? of(this.cachedSessions)
        : this.fetchAndCacheSessions(this.deviceId);

    return sessions$.pipe(
      map((sessions) => {
        this.isEmptySubject.next(sessions.length === 0);

        const filteredSessions = applyActiveFilters(sessions, params.activeFilters);

        const startRow = params.startRow || 0;
        const endRow = params.endRow || filteredSessions.length;
        const pageData = filteredSessions.slice(startRow, endRow);

        return {
          data: pageData,
          itemTotal: filteredSessions.length,
        };
      })
    );
  }

  /**
   * Fetches the full session list for a device from the backend and caches it
   */
  private fetchAndCacheSessions(deviceId: string): Observable<GopherBuddySession[]> {
    this.isLoadingSubject.next(true);

    return this.devicesDataService.getDeviceSessions(deviceId).pipe(
      map((deviceSessions: Record<string, unknown>[] | null) => {
        if (!deviceSessions || deviceSessions.length === 0) {
          return [];
        }

        // Add required 'id' field for CDW Search Grid
        return deviceSessions.map((session, index) => ({
          ...session,
          id: `${session.deviceId || 'unknown'}-${session.startTime || index}`,
        } as GopherBuddySession));
      }),
      tap((sessions) => {
        this.cachedDeviceId = deviceId;
        this.cachedSessions = sessions;
        this.isLoadingSubject.next(false);
      })
    );
  }
}
